import { useEffect, useState } from "react";
import { FaFilePdf, FaFileExcel, FaDownload } from "react-icons/fa";
import FilterTabs from "../shared/FilterTabs";
import ReportPdfPreview, { downloadPdfFromBase64 } from "./ReportPdfPreview";
import ReportSheetsPreview from "./ReportSheetsPreview";
import ReportLoadingState from "./ReportLoadingState";
import ReportErrorState from "./ReportErrorState";
import ReportEmptyState from "./ReportEmptyState";
import { downloadSheetsAsXlsx } from "../../utils/reportSheetsExcel";

/**
 * Müşteri Performans biçimindeki raporun (PDF + `sheets`) ortak görünümü
 * (#338, #343). Rapor sayfası ve firma / cihaz detayının Raporlar sekmesi
 * aynı parçaları kullanır. `report` = sunucu cevabının `data`'sı:
 * { pdfBase64, sheets, fileName, firmName, isDealer, dateRange }.
 */

const hasSheetsOf = (report) => Array.isArray(report?.sheets) && report.sheets.length > 0;

/** "Excel İndir" + "PDF İndir" */
export function ReportDownloadButtons({ report }) {
  if (!report?.pdfBase64) return null;
  return (
    <div className="flex items-center gap-2">
      {hasSheetsOf(report) && (
        <button
          type="button"
          onClick={() => downloadSheetsAsXlsx(report.sheets, report.fileName)}
          title="Excel İndir"
          className="flex items-center gap-1.5 px-2.5 py-2 sm:px-3.5 bg-emerald-700 text-white rounded-lg shadow-sm hover:bg-emerald-800 active:bg-emerald-900 transition-colors text-xs font-SemiBold"
        >
          <FaFileExcel className="w-4 h-4" />
          <FaDownload className="w-3 h-3" />
          <span className="hidden sm:inline">Excel İndir</span>
        </button>
      )}
      <button
        type="button"
        onClick={() => downloadPdfFromBase64(report.pdfBase64, report.fileName)}
        title="PDF İndir"
        className="flex items-center gap-1.5 px-2.5 py-2 sm:px-3.5 bg-red-600 text-white rounded-lg shadow-sm hover:bg-red-700 active:bg-red-800 transition-colors text-xs font-SemiBold"
      >
        <FaFilePdf className="w-4 h-4" />
        <FaDownload className="w-3 h-3" />
        <span className="hidden sm:inline">PDF İndir</span>
      </button>
    </div>
  );
}

/** Önizleme: öncelik Excel (kullanıcı kararı, #338); PDF bir tıkla. */
export function ReportPreview({ report, toolbar = null }) {
  const [mode, setMode] = useState("excel");
  useEffect(() => setMode("excel"), [report]);
  if (!report?.pdfBase64) return null;
  const hasSheets = hasSheetsOf(report);
  const showExcel = hasSheets && mode === "excel";
  return (
    <div className="flex-1 min-h-0 flex flex-col">
      {(hasSheets || toolbar) && (
        <div className="flex items-start justify-between gap-2">
          {hasSheets ? (
            <FilterTabs
              filters={[
                { id: "excel", label: "Excel Önizleme", icon: FaFileExcel },
                { id: "pdf", label: "PDF Önizleme", icon: FaFilePdf },
              ]}
              activeFilter={mode}
              onChange={setMode}
              color="indigo"
            />
          ) : (
            <span />
          )}
          {toolbar}
        </div>
      )}
      {showExcel ? (
        <ReportSheetsPreview sheets={report.sheets} />
      ) : (
        <ReportPdfPreview
          pdfBase64={report.pdfBase64}
          title={`${report.firmName || "Rapor"}${report.isDealer ? " (Bayi)" : ""} — ${
            report.dateRange || ""
          }`}
        />
      )}
    </div>
  );
}

/**
 * Yükleniyor / hata / boş / sonuç — sekmelerde tek parça. Sonuçta indirme
 * düğmeleri önizleme sekmelerinin yanında.
 */
export default function PerformanceReportView({ report, isLoading, error, emptyMessage }) {
  if (isLoading) {
    return (
      <ReportLoadingState
        title="Rapor Hazırlanıyor"
        description="Veriler yükleniyor, lütfen bekleyin..."
        skeletonRows={6}
        skeletonColumns={4}
      />
    );
  }
  if (error) {
    return (
      <ReportErrorState
        title="Hata Oluştu"
        message={error?.data?.message || error?.error || "Rapor oluşturulurken bir hata oluştu."}
        icon="warning"
      />
    );
  }
  if (!report?.pdfBase64) {
    return (
      <ReportEmptyState
        title="Rapor Bekleniyor"
        message={emptyMessage || "Tarih ve ürün seçip 'Raporu Getir' butonuna basın."}
        icon="document"
      />
    );
  }
  return <ReportPreview report={report} toolbar={<ReportDownloadButtons report={report} />} />;
}
